import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, HelpCircle, Sparkles, Lock } from "lucide-react";
import axios from "axios";
import { Sticker } from "@/components/Stickers";
import { SEO } from "@/lib/seo";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public match-reveal landing page.
 *
 * URL: /reveal/:sharerId/:matchId
 *
 * Mystery-by-default treatment:
 *  • Sharer's face & name are always visible (they consented by sharing).
 *  • The MATCH's face is shown ONLY if both sides set `share_opt_in=true` on
 *    Profile. Otherwise we render a glossy "?" silhouette with a Lock icon
 *    chip — preserving curiosity AND privacy.
 *  • No auth required — this is a referral funnel.
 *  • Sharer's referral code is persisted to localStorage so when the viewer
 *    signs up we can credit the source.
 */
export default function Reveal() {
  const { sharerId, matchId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [peek, setPeek] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/share/reveal/${sharerId}/${matchId}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setError("Couldn't load this reveal."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sharerId, matchId]);

  // Attribute the eventual signup to whoever shared this page.
  useEffect(() => {
    if (data?.referral) {
      try { localStorage.setItem("dc_referral", data.referral); } catch (err) { console.warn("referral persist failed", err); }
    }
  }, [data?.referral]);

  const isRevealed = data?.both_opt_in;
  const sharerName = data?.sharer?.name || "Someone";
  const headline = useMemo(() => {
    if (!data) return "Loading…";
    if (isRevealed) return `${sharerName} & ${data.match.name} matched on DoppelCrush`;
    return `${sharerName} found a DoppelCrush`;
  }, [data, isRevealed, sharerName]);

  return (
    <div className="crush-bg min-h-screen">
      <SEO
        title={headline}
        description={data?.cta?.sub || "Tap to see the reveal on DoppelCrush"}
        image="/og-default.png"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16">
        {/* Lightweight branded header (no auth UI — we want the funnel) */}
        <div className="crush-frame flex items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 mb-6" data-testid="reveal-header">
          <Link to="/" className="flex items-center gap-3" data-testid="reveal-home-link">
            <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-2xl overflow-hidden shadow-lg shadow-pink-200">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-300 via-pink-500 to-orange-500" />
              <div className="absolute inset-0 flex items-center justify-center font-display text-white text-xl sm:text-2xl font-bold">D</div>
            </div>
            <div className="leading-tight">
              <div className="font-display text-lg sm:text-xl font-bold text-slate-900">DoppelCrush</div>
              <div className="text-[11px] text-slate-500 font-body">Find your Doppel. Flirt with chaos.</div>
            </div>
          </Link>
          <Link
            to="/auth?mode=signup"
            className="crush-cta rounded-full font-display font-bold px-4 py-2 text-xs sm:text-sm"
            data-testid="reveal-signup-btn"
          >
            Try your own
          </Link>
        </div>

        <div className="crush-frame p-6 sm:p-10 relative overflow-hidden">
          <Sticker kind="heart" size={84} className="absolute -top-6 -right-4 rotate-12 hidden sm:block" />
          <Sticker kind="spark" size={64} className="absolute -bottom-4 -left-4 -rotate-12 hidden sm:block" />

          {loading ? (
            <div className="py-16 text-center font-display text-pink-600">Opening the reveal…</div>
          ) : error ? (
            <div className="py-16 text-center text-rose-600 font-body" data-testid="reveal-error">{error}</div>
          ) : (
            <>
              <div className="text-xs uppercase font-display font-bold tracking-widest text-pink-600">DoppelCrush · Match Reveal</div>
              <h1 className="font-display text-3xl sm:text-5xl font-extrabold leading-tight mt-2 text-slate-900" data-testid="reveal-headline">
                {headline}
              </h1>
              <p className="mt-3 font-body text-slate-600">
                {isRevealed
                  ? "Both of them opted in — say hello to the duo."
                  : data?.cta?.sub || "Tap to see the reveal on DoppelCrush"}
              </p>

              {/* Faces row */}
              <div className="mt-8 flex items-center justify-center gap-6 sm:gap-10" data-testid="reveal-faces">
                <FaceCard
                  src={data.sharer.photo_url}
                  label={data.sharer.name}
                  ringClass="border-pink-300"
                  testid="reveal-face-sharer"
                />
                <Sticker kind="heart" size={64} className="rotate-12 hidden sm:block" />
                {isRevealed ? (
                  <FaceCard
                    src={data.match.photo_url}
                    label={data.match.name}
                    ringClass="border-orange-300"
                    testid="reveal-face-match"
                  />
                ) : (
                  <MysteryCard
                    initial={data.match.initial}
                    onPeek={() => setPeek((p) => !p)}
                    peek={peek}
                  />
                )}
              </div>

              {!isRevealed ? (
                <div className="mt-6 mx-auto max-w-xl rounded-2xl bg-pink-50 border-2 border-pink-100 p-4 sm:p-5 text-center" data-testid="reveal-mystery-explainer">
                  <div className="inline-flex items-center gap-1.5 font-display font-bold text-pink-700">
                    <Lock className="w-4 h-4" /> Mystery match
                  </div>
                  <p className="mt-1 text-sm font-body text-slate-700">
                    {data?.match?.opt_in
                      ? "We're keeping things mysterious for now."
                      : "Their identity stays private until they opt in."}{" "}
                    Sign up and find your own DoppelCrush in 30 seconds.
                  </p>
                </div>
              ) : null}

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                <Link
                  to="/auth?mode=signup"
                  className="crush-cta inline-flex items-center justify-center gap-2 rounded-full font-display font-bold px-6 py-3 text-base"
                  data-testid="reveal-cta-primary"
                >
                  {data?.cta?.button || "Try your own match"} <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/how-it-works"
                  className="crush-secondary inline-flex items-center justify-center gap-2 rounded-full font-display font-bold px-6 py-3 text-base"
                  data-testid="reveal-cta-secondary"
                >
                  How it works
                </Link>
              </div>

              <div className="mt-6 text-center text-[11px] font-body text-slate-400">
                Shared by @{sharerName.toLowerCase().replace(/\s+/g, "")} · Faces shown only with consent.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const FaceCard = ({ src, label, ringClass, testid }) => (
  <div className="text-center" data-testid={testid}>
    <div className={`relative w-28 h-28 sm:w-40 sm:h-40 rounded-3xl overflow-hidden border-4 ${ringClass} shadow-xl bg-white`}>
      {src ? (
        <img src={src} alt={label} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full grid place-items-center bg-gradient-to-br from-pink-200 to-orange-200 font-display text-3xl font-bold text-white">
          {(label || "?").slice(0, 1).toUpperCase()}
        </div>
      )}
    </div>
    <div className="mt-2 font-display font-bold text-slate-900 text-sm sm:text-base">{label}</div>
  </div>
);

const MysteryCard = ({ initial = "?", onPeek, peek }) => (
  <div className="text-center" data-testid="reveal-face-mystery">
    <button
      type="button"
      onClick={onPeek}
      className="relative w-28 h-28 sm:w-40 sm:h-40 rounded-3xl overflow-hidden border-4 border-pink-300 shadow-xl bg-gradient-to-br from-pink-200 via-pink-300 to-orange-300 grid place-items-center group focus:outline-none focus:ring-4 focus:ring-pink-200"
      aria-label="Mystery match"
      data-testid="reveal-mystery-peek-btn"
    >
      {/* Halftone dots for sticker feel */}
      <div
        className="absolute inset-0 opacity-30"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px)", backgroundSize: "10px 10px" }}
        aria-hidden
      />
      <span className="relative font-display text-6xl sm:text-7xl font-extrabold text-white drop-shadow-[0_3px_0_rgba(15,23,42,0.4)]">?</span>
      <span className="absolute bottom-2 inline-flex items-center gap-1 text-[10px] sm:text-xs font-display font-bold bg-slate-900 text-white rounded-full px-2 py-0.5">
        <HelpCircle className="w-3 h-3" /> Hidden
      </span>
      {peek ? (
        <span className="absolute inset-0 grid place-items-center bg-slate-900/85 text-white text-sm font-body p-3">
          They keep it private — only their match sees the face card.
        </span>
      ) : null}
    </button>
    <div className="mt-2 font-display font-bold text-slate-700 text-sm sm:text-base inline-flex items-center gap-1.5">
      <Sparkles className="w-4 h-4 text-pink-500" /> {initial}???
    </div>
  </div>
);
